import { DataGridProps, GridRowModes, GridRowModesModel, useGridApiRef } from '@mui/x-data-grid';
import { SettingsDataGrid } from 'components/atoms/BaseDataGrid/SettingsDataGrid';
import {
  generateGetCellClassNames,
  handleRowModesModelChangeWithoutDraftIds,
} from 'components/atoms/EditableDataGrid/helpers';
import Loader, { LoadingStage } from 'components/Loader';
import { isEmpty } from 'es-toolkit/compat';
import { Permission } from 'interfaces/permissionOption';
import { TaxonomyTag } from 'interfaces/taxonomyTag';
import React from 'react';
import { usePermissions } from 'utils/usePermissions';
import useTaxonomyTags from '../../../../utils/queryHooks/taxonomy/useTaxonomyTags';
import { taxonomyTagFields } from './taxonomyTagFields';
import { TaxonomyTagDraft } from './types';
import { useTaxonomyTagColumns } from './useTaxonomyTagColumns';
import { generateDraftId, getTaxonomyTagId } from './utils';

export const TaxonomyTagsDataGrid: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canEditTaxonomyTags = hasPermission(Permission.EditTaxonomyOptions);

  const { data: dbTaxonomyTags, isLoading: isLoadingTaxonomyTags } = useTaxonomyTags();

  const [draftTaxonomyTags, setDraftTaxonomyTags] = React.useState<TaxonomyTagDraft[]>([]);
  const taxonomyTags = React.useMemo(
    () => [...draftTaxonomyTags, ...(dbTaxonomyTags || [])],
    [dbTaxonomyTags, draftTaxonomyTags]
  );
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

  const handleAddTaxonomyTag = React.useCallback(() => {
    // We use the draftId field to distinguish between draft rows and db rows
    // This is necessary because we need to be able to edit ids in new rows but not in existing rows
    const draftId = generateDraftId();
    const newTaxonomyTag: TaxonomyTagDraft = { displayName: '', id: '', draftId, labels: [] };
    setDraftTaxonomyTags((oldDraftTaxonomyTags) => [newTaxonomyTag, ...oldDraftTaxonomyTags]);
    setRowModesModel((oldRowModesModel) => ({
      ...oldRowModesModel,
      [newTaxonomyTag.draftId]: { mode: GridRowModes.Edit },
    }));
  }, [setDraftTaxonomyTags, setRowModesModel]);

  const apiRef = useGridApiRef();

  const handleRowModesModelChange: DataGridProps<TaxonomyTag>['onRowModesModelChange'] = React.useCallback(
    (newRowModesModel: GridRowModesModel) => {
      handleRowModesModelChangeWithoutDraftIds(
        newRowModesModel,
        setRowModesModel,
        draftTaxonomyTags,
        (row: TaxonomyTagDraft) => row.draftId
      );
    },
    [draftTaxonomyTags]
  );

  const columns = useTaxonomyTagColumns({
    noRows: isEmpty(taxonomyTags),
    apiRef,
    taxonomyTags,
    draftTaxonomyTags,
    rowModesModel,
    setDraftTaxonomyTags,
    setRowModesModel,
    editable: canEditTaxonomyTags,
  });

  const getTaxonomyTagCellClassName: DataGridProps['getCellClassName'] = React.useMemo(
    () =>
      generateGetCellClassNames<TaxonomyTag | TaxonomyTagDraft>({
        apiRef,
        requiredFields: ['id', 'displayName', 'sourceType'],
        uniqueFieldGroups: [['id'], ['displayName']],
        draftRows: draftTaxonomyTags,
        fieldsToCheckForErrors: taxonomyTagFields,
        idGetter: getTaxonomyTagId,
      }),
    [apiRef, draftTaxonomyTags]
  );

  return !isLoadingTaxonomyTags ? (
    <SettingsDataGrid
      showCsvExport
      apiRef={apiRef}
      addText={canEditTaxonomyTags ? 'Add Taxonomy Tag' : undefined}
      filterStateQueryParamKey="operationsSettingsTaxonomyTagFilters"
      handleAdd={canEditTaxonomyTags ? handleAddTaxonomyTag : undefined}
      getCellClassName={getTaxonomyTagCellClassName}
      rows={taxonomyTags}
      columns={columns}
      rowModesModel={rowModesModel}
      onRowModesModelChange={handleRowModesModelChange}
      getRowId={getTaxonomyTagId}
      initialState={{
        sorting: {
          sortModel: [
            {
              field: 'id',
              sort: 'asc',
            },
          ],
        },
      }}
    />
  ) : (
    <Loader stage={LoadingStage.LoadingOperationsSettings} />
  );
};
